import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Save, Loader2, GripVertical, FileText, 
  Package, Info, ClipboardList, Calendar, FolderOpen, FileDown, Eye
} from 'lucide-react';
import { format } from '../utils/dateUtils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

// Sortable release note item
const SortableNote = ({ note, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 p-3 rounded-lg border bg-background"
      data-testid={`release-note-${note.id}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="h-5 w-5" />
      </div>
      <span className="font-mono text-sm text-muted-foreground w-6 shrink-0">{note.order}</span>
      <p className="flex-1 text-sm">{note.content}</p>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive shrink-0"
        onClick={() => onDelete(note.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'very-high', label: 'Very High', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'high', label: 'High', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-300' }
];

export const ReleaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [release, setRelease] = useState(null);
  const [userStories, setUserStories] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [versionForm, setVersionForm] = useState('');
  const [statusForm, setStatusForm] = useState('planned');
  const [noteForm, setNoteForm] = useState({ content: '', order: 1 });
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  };

  const fetchRelease = useCallback(async () => {
    try {
      const [releaseRes, storiesRes] = await Promise.all([
        axios.get(`${API}/releases/${id}`),
        axios.get(`${API}/user-stories/by-release/${id}`)
      ]);
      
      setRelease(releaseRes.data);
      setVersionForm(releaseRes.data.version);
      setStatusForm(releaseRes.data.status || 'planned');
      setUserStories(storiesRes.data);
      
      const maxOrder = releaseRes.data.release_notes?.length > 0
        ? Math.max(...releaseRes.data.release_notes.map(n => n.order)) + 1
        : 1;
      setNoteForm({ content: '', order: maxOrder });
    } catch (error) {
      console.error('Failed to fetch release:', error);
      toast.error('Failed to load release');
      navigate('/releases');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRelease();
    fetchStatuses();
  }, [fetchRelease]);

  const handleSaveVersion = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/releases/${id}`, { 
        version: versionForm,
        status: statusForm
      });
      toast.success('Release updated');
      fetchRelease();
    } catch (error) {
      toast.error('Failed to update release');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    setAddingNote(true);
    try {
      await axios.post(`${API}/releases/${id}/notes`, noteForm);
      toast.success('Release note added');
      fetchRelease();
    } catch (error) {
      toast.error('Failed to add release note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this release note?')) return;
    try {
      await axios.delete(`${API}/releases/${id}/notes/${noteId}`);
      toast.success('Release note deleted');
      fetchRelease();
    } catch (error) {
      toast.error('Failed to delete release note');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const notes = release.release_notes || [];
    const oldIndex = notes.findIndex(n => n.id === active.id);
    const newIndex = notes.findIndex(n => n.id === over.id);
    
    const reorderedNotes = arrayMove(notes, oldIndex, newIndex);
    
    // Update local state immediately for smooth UX
    setRelease(prev => ({
      ...prev,
      release_notes: reorderedNotes.map((note, idx) => ({ ...note, order: idx + 1 }))
    }));

    // Save new order to backend
    setReordering(true);
    try {
      await axios.put(`${API}/releases/${id}/notes/reorder`, {
        note_ids: reorderedNotes.map(n => n.id)
      });
      toast.success('Notes reordered');
    } catch (error) {
      toast.error('Failed to save order');
      fetchRelease(); // Revert on error
    } finally {
      setReordering(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = statuses.find(s => s.key === status);
    if (statusInfo) {
      return (
        <Badge 
          variant="secondary"
          style={{ backgroundColor: statusInfo.color + '20', color: statusInfo.color }}
        >
          {statusInfo.name}
        </Badge>
      );
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority) || PRIORITIES[3];
    return (
      <Badge variant="outline" className={`${p.color} border text-xs`}>
        {p.label}
      </Badge>
    );
  };

  const getReleaseStatusBadge = (status) => {
    const styles = {
      planned: 'bg-blue-100 text-blue-800',
      doing: 'bg-yellow-100 text-yellow-800',
      done: 'bg-green-100 text-green-800'
    };
    return <Badge variant="secondary" className={styles[status] || styles.planned}>{status}</Badge>;
  };

  const renderStoryGroup = (stories, title, emptyMessage) => {
    if (!stories || stories.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-4">{emptyMessage}</p>;
    }
    return (
      <div className="space-y-2">
        {stories.map(story => (
          <Link 
            key={story.id}
            to={`/user-stories/${story.id}`}
            className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="issue-number text-xs">{story.issue_number}</span>
              <span className="text-sm font-medium">{story.title}</span>
              {getPriorityBadge(story.priority)}
            </div>
            {getStatusBadge(story.status)}
          </Link>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-8 w-8" /></div>;
  }

  if (!release) return null;

  const totalStories = userStories ? Object.values(userStories).reduce((sum, arr) => sum + arr.length, 0) : 0;
  const sortedNotes = [...(release.release_notes || [])].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6" data-testid="release-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/releases')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="font-mono">v{release.version}</Badge>
            {getReleaseStatusBadge(release.status)}
          </div>
          <h1 className="text-2xl font-semibold">{release.project_name}</h1>
        </div>
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => setReportDialogOpen(true)}
          data-testid="release-report-btn"
        >
          <Eye className="h-4 w-4" />
          Release Report
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
            <Info className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="stories" className="gap-2" data-testid="tab-stories">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Stories</span>
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{totalStories}</Badge>
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Notes</span>
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{sortedNotes.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Release Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Release Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input 
                      id="version" 
                      value={versionForm} 
                      onChange={(e) => setVersionForm(e.target.value)} 
                      data-testid="version-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={statusForm} onValueChange={setStatusForm}>
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="planned">Planned</SelectItem>
                        <SelectItem value="doing">Doing</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleSaveVersion} disabled={saving} className="gap-2 w-full" data-testid="save-release-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Project</Label>
                    <p className="font-medium flex items-center gap-2 mt-1">
                      <FolderOpen className="h-4 w-4 text-muted-foreground" />
                      {release.project_name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Version</Label>
                    <p className="font-mono mt-1">v{release.version}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Status</Label>
                    <div className="mt-1">{getReleaseStatusBadge(release.status)}</div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Created</Label>
                    <p className="text-sm flex items-center gap-2 mt-1">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {format(new Date(release.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-primary">{totalStories}</p>
                    <p className="text-xs text-muted-foreground">Stories</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{sortedNotes.length}</p>
                    <p className="text-xs text-muted-foreground">Notes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* User Stories Tab */}
        <TabsContent value="stories" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                User Stories ({totalStories})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {totalStories === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No user stories linked to this release yet.</p>
                  <Link to="/user-stories">
                    <Button variant="outline" className="mt-4">View All Stories</Button>
                  </Link>
                </div>
              ) : (
                <>
                  {statuses.map((status, index) => (
                    <div key={status.key}>
                      {index > 0 && <Separator className="mb-6" />}
                      <div>
                        <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: status.color }}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }} />
                          {status.name} ({userStories?.[status.key]?.length || 0})
                        </h4>
                        {renderStoryGroup(userStories?.[status.key], status.name, `No ${status.name.toLowerCase()} stories`)}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Release Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Release Notes
                {reordering && <Loader2 className="h-4 w-4 animate-spin" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {sortedNotes.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No release notes yet. Add your first note below.</p>
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sortedNotes.map(n => n.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {sortedNotes.map((note) => (
                        <SortableNote key={note.id} note={note} onDelete={handleDeleteNote} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              {sortedNotes.length > 0 && (
                <p className="text-xs text-muted-foreground">Drag notes to reorder them</p>
              )}

              <Separator className="my-4" />

              <form onSubmit={handleAddNote}>
                <Label className="text-sm font-medium mb-3 block">Add Release Note</Label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      value={noteForm.content}
                      onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                      placeholder="Release note content..."
                      required
                      data-testid="note-content-input"
                    />
                  </div>
                  <Button type="submit" disabled={addingNote} className="gap-2" data-testid="add-note-btn">
                    {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Add
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Release Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Release Report - v{release?.version}
            </DialogTitle>
            <DialogDescription>
              {release?.project_name} • {totalStories} user stories
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {userStories && Object.values(userStories).flat().length > 0 ? (
              <div className="space-y-6">
                {/* Group stories by priority */}
                {PRIORITIES.map(priority => {
                  const priorityStories = Object.values(userStories).flat().filter(s => s.priority === priority.value);
                  if (priorityStories.length === 0) return null;
                  
                  return (
                    <div key={priority.value}>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className={`${priority.color} border`}>
                          {priority.label}
                        </Badge>
                        <span className="text-sm text-muted-foreground">({priorityStories.length})</span>
                      </div>
                      <div className="space-y-2 pl-2 border-l-2 border-muted">
                        {priorityStories.map((story) => (
                          <div key={story.id} className="py-1">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="font-mono text-xs shrink-0">
                                {story.issue_number}
                              </Badge>
                              <span className="text-sm">{story.title}</span>
                            </div>
                            {story.release_notes && (
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1 ml-1 pl-3 border-l-2 border-muted">
                                {story.release_notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No user stories in this release</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              Close
            </Button>
            <Button 
              className="gap-2"
              onClick={async () => {
                setGeneratingPdf(true);
                try {
                  // Generate PDF using backend
                  const response = await axios.get(`${API}/releases/${id}/pdf`, {
                    responseType: 'blob'
                  });
                  
                  // Create download link
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `release-${release.version}-report.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  
                  toast.success('PDF downloaded');
                } catch (error) {
                  console.error('PDF generation failed:', error);
                  toast.error('Failed to generate PDF');
                } finally {
                  setGeneratingPdf(false);
                }
              }}
              disabled={generatingPdf || !userStories || Object.values(userStories).flat().length === 0}
              data-testid="download-pdf-btn"
            >
              {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Download PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
